import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "sonner";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Inventory from "@/pages/Inventory";
import StockAdjustments from "@/pages/StockAdjustments";
import StockHistory from "@/pages/StockHistory";
import POS from "@/pages/POS";
import SalesHistory from "@/pages/SalesHistory";
import Clients from "@/pages/Clients";
import ClientProfile from "@/pages/ClientProfile";
import CreditLedger from "@/pages/CreditLedger";
import Suppliers from "@/pages/Suppliers";
import SupplierProfile from "@/pages/SupplierProfile";
import Purchases from "@/pages/Purchases";
import Payables from "@/pages/Payables";
import Expenses from "@/pages/Expenses";
import Reports from "@/pages/Reports";
import ProfitLoss from "@/pages/ProfitLoss";
import Financials from "@/pages/Financials";
import Settings from "@/pages/Settings";
import Backups from "@/pages/Backups";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <div className="min-h-screen grid place-items-center text-agri-ink-2">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

const MGR = ["admin", "manager"];
const ADMIN = ["admin"];

// Blocks direct-URL access to pages the current role may not use.
function RoleRoute({ roles, children }) {
  const { user } = useAuth();
  if (user === undefined) return null;
  if (!user || !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route element={<Protected><Layout /></Protected>}>
            <Route path="/" element={<Dashboard />} />
            <Route path="/inventory" element={<Inventory />} />
            <Route path="/inventory/adjust" element={<RoleRoute roles={MGR}><StockAdjustments /></RoleRoute>} />
            <Route path="/inventory/history" element={<StockHistory />} />
            <Route path="/pos" element={<POS />} />
            <Route path="/sales" element={<SalesHistory />} />
            <Route path="/clients" element={<Clients />} />
            <Route path="/clients/:id" element={<ClientProfile />} />
            <Route path="/credit-ledger" element={<CreditLedger />} />
            <Route path="/suppliers" element={<RoleRoute roles={MGR}><Suppliers /></RoleRoute>} />
            <Route path="/suppliers/:id" element={<RoleRoute roles={MGR}><SupplierProfile /></RoleRoute>} />
            <Route path="/payables" element={<RoleRoute roles={MGR}><Payables /></RoleRoute>} />
            <Route path="/purchases" element={<RoleRoute roles={MGR}><Purchases /></RoleRoute>} />
            <Route path="/expenses" element={<RoleRoute roles={MGR}><Expenses /></RoleRoute>} />
            <Route path="/reports" element={<RoleRoute roles={MGR}><Reports /></RoleRoute>} />
            <Route path="/profit" element={<RoleRoute roles={MGR}><ProfitLoss /></RoleRoute>} />
            <Route path="/financials" element={<RoleRoute roles={MGR}><Financials /></RoleRoute>} />
            <Route path="/settings" element={<RoleRoute roles={MGR}><Settings /></RoleRoute>} />
            <Route path="/settings/backups" element={<RoleRoute roles={ADMIN}><Backups /></RoleRoute>} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
